import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { Box, PopperProps } from '@mui/material';

import {
    BaseDropdownButton,
    BaseDropdownButtonProps,
} from '../BaseDropdownButton';
import { DropdownPopper, DropdownPopperProps } from '../DropdownPopper';


export const useOpenState = (onOpen?: () => void, onClose?: () => void) => {
    const [isOpen, setOpen] = useState(() => false);

    const change = useCallback(() => {
        if (onOpen && !isOpen)
            onOpen();
        if (onClose && isOpen)
            onClose();
        setOpen(!isOpen);
    }, [isOpen, onOpen, onClose]);

    const open = useCallback(() => {
        if (onOpen)
            onOpen();
        setOpen(true);
    }, [onOpen]);

    const close = useCallback(() => {
        setOpen(false);
        if (onClose)
            onClose();
    }, [onClose]);

    return {
        isOpen,
        change,
        close,
        open,
    };
};

export type DropdownButtonProps = Pick<
BaseDropdownButtonProps,
| 'fullWidth'
| 'disabled'
| 'isLoading'
| 'endIcon'
| 'isActive'
| 'typographyProps'
| 'sx'
| 'onClick'
| 'variant'
| 'size'
> & DropdownPopperProps & {
    text: ReactNode;

    popperProps?: Pick<PopperProps, 'disablePortal' | 'sx'>;
};

export const DropdownButton: FC<DropdownButtonProps> = ({
    text,
    isOpen,
    close,
    children,
    popperProps,
    ...buttonProps
}) => {
    const containerRef = useRef<HTMLDivElement>();

    return (
        <Box ref={containerRef} sx={{ position: 'relative' }}>
            <BaseDropdownButton
                isOpen={isOpen}
                {...buttonProps}
            >
                {text}
            </BaseDropdownButton>

            <DropdownPopper
                close={close}
                isOpen={isOpen}
                anchorEl={containerRef.current}
                disablePortal={popperProps?.disablePortal}
                sx={popperProps?.sx}
            >
                {children}
            </DropdownPopper>
        </Box>
    );
};
